import * as tslib_1 from "tslib";
import './image.less';
import { m } from '../hyperscript';
import { addClasses } from '../util/classes';
import { contain, cover } from '../util/fit';
import { createContext } from './context';
import { View } from './view';
var supportsObjectFit = window.navigator.msMaxTouchPoints === undefined;
var canUpscale = function (fit, container, image) {
    var test = fit === 'cover' ? cover : contain;
    var _a = test(container.width, container.height, image.width, image.height), width = _a.width, height = _a.height;
    return width < image.width && height < image.height;
};
var BrowserImage = window['Image'];
var ImageBase = /** @class */ (function (_super) {
    tslib_1.__extends(ImageBase, _super);
    function ImageBase() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.onUpdate = function () {
            _this.load();
            _this.scale();
        };
        return _this;
    }
    ImageBase.prototype.onCreate = function () {
        var _this = this;
        this.load();
        if (!this.shouldCheckScale)
            return;
        var listener = function () { return _this.scale(); };
        window.addEventListener('resize', listener);
        this.onRemove = function () { return window.removeEventListener('resize', listener); };
        this.scale();
    };
    ImageBase.prototype.load = function () {
        var _this = this;
        var _a = this.attrs, onload = _a.onload, onerror = _a.onerror, src = _a.src;
        if (!onload && !onerror)
            return;
        if (this.loader && this.loader.src === src)
            return;
        if (!this.loader) {
            var img = new BrowserImage();
            img.onload = function (e) { return _this.attrs.onload && _this.attrs.onload(e); };
            img.onerror = function (e) { return _this.attrs.onerror && _this.attrs.onerror(e); };
            this.loader = img;
        }
        this.loader.src = src;
    };
    ImageBase.prototype.scale = function () {
        var _a = this.attrs, _b = _a.width, width = _b === void 0 ? 0 : _b, _c = _a.height, height = _c === void 0 ? 0 : _c, fit = _a.fit, background = _a.background;
        if (!this.dom || !this.shouldCheckScale)
            return;
        var container = this.dom.getBoundingClientRect();
        if (canUpscale(fit, container, { width: width, height: height }))
            return;
        if (this.useBackground)
            this.dom.style.backgroundSize = 'auto';
        else
            this.dom.style.objectFit = 'none';
    };
    Object.defineProperty(ImageBase.prototype, "shouldCheckScale", {
        get: function () {
            var _a = this.attrs, width = _a.width, height = _a.height, fit = _a.fit, upScale = _a.upScale;
            var crop = fit === 'cover' || fit === 'contain';
            return !upScale && width && height && crop;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ImageBase.prototype, "shouldCrop", {
        get: function () {
            var _a = this.attrs, _b = _a.fit, fit = _b === void 0 ? 'landscape' : _b, background = _a.background;
            return background || fit === 'cover' || fit === 'contain';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ImageBase.prototype, "useBackground", {
        get: function () {
            var background = this.attrs.background;
            return background || (!supportsObjectFit && this.shouldCrop);
        },
        enumerable: true,
        configurable: true
    });
    ImageBase.prototype.render = function () {
        var _a = this.attrs, src = _a.src, children = _a.children, _b = _a.fit, fit = _b === void 0 ? 'landscape' : _b, upScale = _a.upScale, background = _a.background, _c = _a.position, position = _c === void 0 ? 'center center' : _c, _d = _a.alt, alt = _d === void 0 ? '' : _d, width = _a.width, height = _a.height, style = _a.style, onload = _a.onload, onerror = _a.onerror, 
        // These get passed by sizeof-loader, but we don't want them to be passed
        // to the dom
        type = _a.type, bytes = _a.bytes, attrs = tslib_1.__rest(_a, ["src", "children", "fit", "upScale", "background", "position", "alt", "width", "height", "style", "onload", "onerror", "type", "bytes"]);
        var crop = this.shouldCrop;
        var tag = this.useBackground ? 'div' : 'img';
        var focus = typeof position === 'string'
            ? position
            : position && position.x * 100 + "% " + position.y * 100 + "%";
        return m(tag, tslib_1.__assign({}, (this.useBackground
            ? {
                role: 'img',
                'aria-label': alt,
                style: tslib_1.__assign({ backgroundImage: "url('" + src + "')", backgroundSize: crop && fit, backgroundPosition: focus, backgroundRepeat: 'no-repeat' }, style)
            }
            : {
                src: src,
                alt: alt,
                width: width,
                height: height,
                style: tslib_1.__assign({ objectFit: crop && fit, objectPosition: focus !== 'center center' && focus }, style)
            }), addClasses(attrs, 'image', { mod: { crop: crop } })), children);
    };
    return ImageBase;
}(View));
var ImageResizerContext = createContext();
var ImageResizer = /** @class */ (function (_super) {
    tslib_1.__extends(ImageResizer, _super);
    function ImageResizer() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    ImageResizer.prototype.render = function () {
        var resize = this.attrs.resize;
        return m(ImageResizerContext.Provider, { value: resize }, this.children);
    };
    return ImageResizer;
}(View));
export { ImageResizer };
var Image = /** @class */ (function (_super) {
    tslib_1.__extends(Image, _super);
    function Image() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Image.prototype.render = function () {
        var _this = this;
        var _a = this.attrs, children = _a.children, src = _a.src, width = _a.width, height = _a.height, fit = _a.fit, position = _a.position, upScale = _a.upScale, background = _a.background, _b = _a.alt, alt = _b === void 0 ? '' : _b, type = _a.type, bytes = _a.bytes, style = _a.style, rest = tslib_1.__rest(_a, ["children", "src", "width", "height", "fit", "position", "upScale", "background", "alt", "type", "bytes", "style"]);
        var crop = background || fit === 'cover' || fit === 'contain';
        return m(ImageResizerContext.Consumer, function (resize) {
            if (!resize)
                return m(ImageBase, _this.attrs, children);
            var resolve = function (dom) {
                _this.resolved = resize(_this.attrs, {
                    width: dom.offsetWidth,
                    height: dom.offsetHeight
                });
                _this.cached = src;
            };
            if (!_this.dom)
                return m('div', tslib_1.__assign({}, addClasses(rest, 'image', { mod: { crop: crop } }), { oncreate: function (vnode) {
                        resolve(vnode.dom);
                        m.redraw();
                    } }), children);
            if (_this.cached !== src)
                resolve(_this.dom);
            return _this.resolved && m(ImageBase, _this.resolved, children);
        });
    };
    return Image;
}(View));
export { Image };
